import React, { useState, useEffect } from 'react';
import { Youtube, Plus, Trash2, Loader2, Link as LinkIcon, Play, Lock } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import useKeepAwake from '../hooks/useKeepAwake';
import KeepAwakeToggle from './KeepAwakeToggle';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const YouTubeSection = () => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [newVideo, setNewVideo] = useState({ title: '', youtube_url: '', description: '' });
  const [keepAwake, toggleKeepAwake] = useKeepAwake('youtube', !!selectedVideo);

  useEffect(() => {
    fetchVideos();
  }, []);

  const fetchVideos = async () => {
    try {
      const response = await axios.get(`${API}/videos`);
      setVideos(response.data);
      if (response.data.length > 0 && !selectedVideo) {
        setSelectedVideo(response.data[0]);
      }
    } catch (error) {
      console.error('Error fetching videos:', error);
    } finally {
      setLoading(false);
    }
  };

  const extractVideoId = (url) => {
    const regex = /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/;
    const match = url.match(regex);
    return match ? match[1] : null;
  };

  const handleAddVideo = async () => {
    if (!newVideo.youtube_url || !newVideo.title) {
      toast.error('Veuillez entrer un titre et une URL YouTube');
      return;
    }

    const videoId = extractVideoId(newVideo.youtube_url);
    if (!videoId) {
      toast.error('URL YouTube invalide');
      return;
    }

    setAdding(true);
    try {
      await axios.post(`${API}/videos`, newVideo);
      toast.success('Vidéo ajoutée avec succès');
      setNewVideo({ title: '', youtube_url: '', description: '' });
      setShowAddForm(false);
      fetchVideos();
    } catch (error) {
      console.error('Error adding video:', error);
      toast.error('Erreur lors de l\'ajout de la vidéo');
    } finally {
      setAdding(false);
    }
  };

  const handleDeleteVideo = async (videoId) => {
    if (!window.confirm('Supprimer cette vidéo ?')) return;
    
    try {
      await axios.delete(`${API}/videos/${videoId}`);
      toast.success('Vidéo supprimée');
      if (selectedVideo?.id === videoId) {
        setSelectedVideo(null);
      }
      fetchVideos();
    } catch (error) {
      console.error('Error deleting video:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  return (
    <div className="space-y-6" data-testid="youtube-section">
      {/* Video Player */}
      <div className="player-card p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-lg font-semibold text-white flex items-center gap-2">
            <Youtube className="w-5 h-5 text-red-500" />
            Vidéos YouTube
          </h3>
          <Button
            onClick={() => setShowAddForm(!showAddForm)}
            variant="outline"
            size="sm"
            className="border-white/20 text-white hover:bg-white/10"
            data-testid="add-video-btn"
          >
            <Plus className="w-4 h-4 mr-1" />
            Ajouter
          </Button>
        </div>

        {/* Add Video Form */}
        {showAddForm && (
          <div className="mb-6 p-4 bg-white/5 rounded-xl border border-white/10 space-y-3">
            <Input
              placeholder="Titre de la vidéo *"
              value={newVideo.title}
              onChange={(e) => setNewVideo(prev => ({ ...prev, title: e.target.value }))}
              className="bg-white/5 border-white/10 text-white placeholder:text-white/40"
              data-testid="video-title-input"
            />
            <div className="relative">
              <LinkIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
              <Input
                placeholder="URL YouTube (ex: https://youtube.com/watch?v=...)"
                value={newVideo.youtube_url}
                onChange={(e) => setNewVideo(prev => ({ ...prev, youtube_url: e.target.value }))}
                className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-white/40"
                data-testid="video-url-input"
              />
            </div>
            <Input
              placeholder="Description (optionnel)"
              value={newVideo.description}
              onChange={(e) => setNewVideo(prev => ({ ...prev, description: e.target.value }))}
              className="bg-white/5 border-white/10 text-white placeholder:text-white/40"
              data-testid="video-description-input"
            />
            <div className="flex gap-2">
              <Button
                onClick={handleAddVideo}
                disabled={adding}
                className="flex-1 bg-red-600 hover:bg-red-500"
                data-testid="save-video-btn"
              >
                {adding ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  'Ajouter la vidéo'
                )}
              </Button>
              <Button
                onClick={() => setShowAddForm(false)}
                variant="outline"
                className="border-white/20 text-white hover:bg-white/10"
              >
                Annuler
              </Button>
            </div>
          </div>
        )}

        {/* YouTube Player - Using native iframe for reliability */}
        {selectedVideo ? (
          <>
          <div className="mb-3 flex justify-end">
            <KeepAwakeToggle keepAwake={keepAwake} onToggle={toggleKeepAwake} testId="keep-awake-youtube"
              className="px-3 py-1.5 rounded-full text-xs font-medium bg-white/10 border border-white/15 text-white/80 hover:bg-white/20" />
          </div>
          <div className="youtube-container aspect-video mb-4 rounded-xl overflow-hidden">
            <iframe
              src={`https://www.youtube.com/embed/${extractVideoId(selectedVideo.youtube_url)}?rel=0&modestbranding=1`}
              title={selectedVideo.title}
              width="100%"
              height="100%"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="w-full h-full"
            />
          </div>
          </>
        ) : (
          <div className="aspect-video bg-white/5 rounded-xl flex items-center justify-center mb-4">
            <p className="text-white/40">Sélectionnez ou ajoutez une vidéo</p>
          </div>
        )}

        {selectedVideo && (
          <div className="mb-4">
            <h4 className="font-heading text-white font-medium">{selectedVideo.title}</h4>
            {selectedVideo.description && (
              <p className="text-white/60 text-sm mt-1">{selectedVideo.description}</p>
            )}
          </div>
        )}
      </div>

      {/* Video List */}
      <div className="player-card p-6">
        <h3 className="font-heading text-lg font-semibold text-white mb-4">
          Liste des Vidéos
        </h3>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 text-amber-400 animate-spin" />
          </div>
        ) : videos.length === 0 ? (
          <p className="text-white/60 text-center py-8">
            Aucune vidéo. Ajoutez votre première vidéo YouTube !
          </p>
        ) : (
          <div className="space-y-3">
            {videos.map((video) => {
              const isSelected = selectedVideo?.id === video.id;
              const videoId = extractVideoId(video.youtube_url);
              const thumbnailUrl = videoId 
                ? `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`
                : null;
              
              return (
                <div
                  key={video.id}
                  className={`track-item cursor-pointer ${isSelected ? 'playing' : ''}`}
                  onClick={() => setSelectedVideo(video)}
                  data-testid={`video-item-${video.id}`}
                >
                  {thumbnailUrl && (
                    <img
                      src={thumbnailUrl}
                      alt={video.title}
                      className="w-24 h-14 object-cover rounded-lg flex-shrink-0"
                    />
                  )}
                  
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-medium truncate">{video.title}</p>
                    {video.description && (
                      <p className="text-white/50 text-sm truncate">{video.description}</p>
                    )}
                  </div>
                  
                  {video.locked ? (
                    <div 
                      className="p-2 text-yellow-400"
                      title="Vidéo verrouillée"
                    >
                      <Lock className="w-5 h-5" />
                    </div>
                  ) : (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteVideo(video.id);
                      }}
                      className="p-2 text-white/40 hover:text-red-500 hover:bg-red-500/10 rounded-lg transition-all"
                      title="Supprimer cette vidéo"
                      data-testid={`delete-video-${video.id}`}
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default YouTubeSection;
